import I18n from 'I18n';
import { useMemo } from 'react';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { ApprovalOperation } from '../../types';
import { ApprovalRulesRadio } from '../ApprovalRulesRadio';
import { ApproverSelect } from '../ApproverSelect';
import { ApprovalModalMessage } from './ApprovalModalMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { ApprovalModalMessageLabelHelpI18n, ApprovalModalMessageLabelI18n } from '../i18n/ApprovalModalMessageLabelI18n';
import { ApprovalDataErrorMessage } from '../sidebar/ApprovalDataErrorMessage';
import { View } from 'foundations-components/transitional/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ApprovalDueDateInputGroup } from '../ApprovalDueDateInputGroup';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { useGetIsUngated } from '../../hooks/useGetIsUngated';
import { GATES } from '../../constants/auth';
import { ApprovalTitleTextInput } from '../ApprovalTitleTextInput';
import { MAX_APPROVAL_NOTE_LENGTH } from '../../constants/requestModal';
import { useApprovers } from '../../hooks/useApprovers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LazyApprovalNoteEditor = Loadable({
  loader: () => import( /* webpackChunkName: "ApprovalNoteEditor" */'../common/ApprovalNoteEditor').then(m => ({
    default: m.ApprovalNoteEditor
  })),
  LoadingComponent: () => /*#__PURE__*/_jsx(LoadingSpinner, {
    layout: "centered",
    size: "small"
  }),
  ErrorComponent: ApprovalDataErrorMessage
});
export const ApprovalModalBody = ({
  alertContent,
  currentOperation,
  approverActorIds,
  availableApprovalRules,
  isLoading,
  note,
  onChangeApprovers,
  onChangeApprovalRule,
  onChangeNote,
  isNoteTooLong,
  dueDate,
  setDueDate,
  dueTime,
  setDueTime,
  isDueDateValid,
  setIsDueDateValid,
  onNoteOverLimit,
  approvalTitle,
  canEditApprovalTitle,
  onChangeApprovalTitle
}) => {
  const approval = useApprovalContext();
  const {
    approvalType,
    approvalAction
  } = approval.meta;
  const getHasGate = useGetIsUngated();
  const hasRichTextGate = getHasGate(GATES.REQUEST_NOTE_RICH_TEXT);
  const onChangeNoteEvent = evt => onChangeNote(evt.target.value);
  const {
    approvers,
    isLoading: isLoadingApprovers,
    isError: isFetchApproversError
  } = useApprovers(approvalType, approvalAction);
  const isRequestOperation = currentOperation === ApprovalOperation.REQUEST;
  const noteIsRequired = useMemo(() => currentOperation === ApprovalOperation.CANCEL || currentOperation === ApprovalOperation.REJECT || currentOperation === ApprovalOperation.REQUEST_CHANGES, [currentOperation]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(TFlex, {
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      })
    });
  }
  if (isRequestOperation && isLoadingApprovers) {
    return /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(TFlex, {
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      })
    });
  }
  if (isRequestOperation && isFetchApproversError) {
    return /*#__PURE__*/_jsx(DialogBody, {
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModalBody.fetchError.title"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModalBody.fetchError.description"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(DialogBody, {
    children: [/*#__PURE__*/_jsxs(View, {
      "data-test-id": "product-approvals-approval-modal-body",
      marginBlockEnd: "300",
      as: "div",
      children: [alertContent && /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        as: "div",
        children: alertContent
      }), /*#__PURE__*/_jsx(ApprovalModalMessage, {})]
    }), isRequestOperation && /*#__PURE__*/_jsxs(_Fragment, {
      children: [canEditApprovalTitle && /*#__PURE__*/_jsx(ApprovalTitleTextInput, {
        approvalTitle: approvalTitle,
        onChangeApprovalTitle: onChangeApprovalTitle
      }), /*#__PURE__*/_jsx(ApproverSelect, {
        approvers: approvers,
        value: approverActorIds,
        onChange: onChangeApprovers
      }), availableApprovalRules.length > 1 && /*#__PURE__*/_jsx(ApprovalRulesRadio, {
        approvalType: approval.meta.approvalType,
        onChangeApprovalRule: onChangeApprovalRule
      }), /*#__PURE__*/_jsx(ApprovalDueDateInputGroup, {
        dueDate: dueDate,
        setDueDate: setDueDate,
        dueTime: dueTime,
        setDueTime: setDueTime,
        isDueDateValid: isDueDateValid,
        setIsDueDateValid: setIsDueDateValid
      })]
    }), /*#__PURE__*/_jsx(FormControl, {
      characterCount: hasRichTextGate ? undefined : MAX_APPROVAL_NOTE_LENGTH - note.length,
      help: /*#__PURE__*/_jsx(ApprovalModalMessageLabelHelpI18n, {
        currentOperation: currentOperation,
        approvalType: approval.meta.approvalType
      }),
      label: /*#__PURE__*/_jsx(ApprovalModalMessageLabelI18n, {
        currentOperation: currentOperation,
        approvalType: approval.meta.approvalType
      }),
      required: noteIsRequired,
      validationMessage: isNoteTooLong && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.approvalModalNoteError.exceedsMaxLength",
        options: {
          maxLength: MAX_APPROVAL_NOTE_LENGTH
        }
      }),
      error: isNoteTooLong,
      children: hasRichTextGate ? /*#__PURE__*/_jsx(LazyApprovalNoteEditor, {
        "data-test-id": `product-approvals-approval-note-${currentOperation}`,
        placeholder: noteIsRequired ? I18n.text('productApprovals.approvalModalMessagePlaceholder') : I18n.text('productApprovals.approvalModalMessagePlaceholderOptional'),
        onChangeNote: onChangeNote,
        charLimit: MAX_APPROVAL_NOTE_LENGTH,
        onOverLimit: onNoteOverLimit
      }) : /*#__PURE__*/_jsx(TextArea, {
        role: "textbox",
        "data-test-id": `product-approvals-approval-note-${currentOperation}`,
        rows: 4,
        placeholder: I18n.text(noteIsRequired ? 'productApprovals.approvalModalMessagePlaceholder' : 'productApprovals.approvalModalMessagePlaceholderOptional'),
        value: note,
        onChange: onChangeNoteEvent
      })
    })]
  });
};